import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  TextInput as RNTextInput,
  StyleSheet,
  Platform,
  View
} from "react-native";
import { colors, typography, layout, webScrollbarStyles } from "@/constants";

/**
 * Props interface for the ChatTextInput component
 */
interface ChatTextInputProps {
  /** Current text inputText in the input field */
  inputText: string;
  /** Placeholder text to show when input is empty */
  placeholderText?: string;
  /** Maximum number of characters allowed */
  maxLength?: number;
  /** Custom style to override default styling */
  style?: object;
  /** Callback function when text changes */
  onChangeText: (text: string) => void;
  /** Callback function when submit/return is pressed */
  onSubmitEditing?: () => void;
  /** Callback function when input gains focus */
  onFocus?: () => void;
  /** Callback function when input loses focus */
  onBlur?: () => void;
}

const isDesktopWeb =
  Platform.OS === "web" && !/iPhone|Android|Mobile/i.test(navigator.userAgent);

/**
 * A specialized text input component optimized for chat messaging.
 * Features Chrome Android keyboard scroll behavior and chat-specific styling.
 *
 * @param props - The component props
 * @returns A styled TextInput optimized for chat interactions
 */
export default function ChatTextInput({
  inputText,
  placeholderText = "Type your message...",
  maxLength = 2000,
  style,
  onChangeText,
  onSubmitEditing,
  onFocus,
  onBlur
}: ChatTextInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<RNTextInput>(null);

  // Compute dynamic heights based on typography tokens
  const fontSize = typography.fontSizes.medium;
  const lineHeight = Math.round(fontSize * typography.lineHeights.normal);
  const verticalPadding = layout.space.md; // must match paddingVertical in styles.textInput
  const MIN_HEIGHT = verticalPadding * 2 + lineHeight; // 1 line
  const MAX_HEIGHT = verticalPadding * 2 + lineHeight * 6; // up to 6 lines

  const [inputHeight, setInputHeight] = useState<number>(MIN_HEIGHT);

  const handleKeyPress = useCallback(
    ({ nativeEvent }: any) => {
      if (nativeEvent.key === "Enter" && !nativeEvent.shiftKey) {
        nativeEvent.preventDefault?.();
        onSubmitEditing?.();
      }
    },
    [onSubmitEditing]
  );

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();

    // Chrome Android: Scroll input into view when keyboard appears
    if (Platform.OS === "web") {
      const userAgent = window.navigator.userAgent;
      const isChromeAndroid =
        /Android/.test(userAgent) &&
        /Chrome/.test(userAgent) &&
        !/Edg/.test(userAgent);

      if (isChromeAndroid) {
        setTimeout(() => {
          const inputElement = inputRef.current;
          if (inputElement) {
            // On web, the ref directly points to the DOM element
            const domElement = inputElement as unknown as HTMLElement;
            if (domElement && domElement.scrollIntoView) {
              domElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest"
              });
            }
          }
        }, 300); // Longer delay to ensure keyboard is fully visible
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const handleContentSizeChange = useCallback(
    (e: any) => {
      const contentHeight = e?.nativeEvent?.contentSize?.height ?? MIN_HEIGHT;
      const clampedHeight = Math.max(
        MIN_HEIGHT,
        Math.min(MAX_HEIGHT, Math.ceil(contentHeight))
      );
      setInputHeight(clampedHeight);
    },
    [MIN_HEIGHT, MAX_HEIGHT]
  );

  // Reset height to one line when input is cleared
  useEffect(() => {
    if (!inputText) {
      setInputHeight(MIN_HEIGHT);
    }
  }, [inputText, MIN_HEIGHT]);

  // Web-specific style for scrollbar
  const isAtMaxHeight = inputHeight >= MAX_HEIGHT;
  const webScrollbarStyle =
    Platform.OS === "web"
      ? {
          // Only show internal vertical scrollbar when at max height
          // @ts-ignore - web-only CSS property
          overflowY: isAtMaxHeight ? "auto" : "hidden",
          // Extra padding for scrollbar space only when scrollbar can appear
          paddingRight: isAtMaxHeight ? 20 : 0,
          // @ts-ignore - disable manual resize on web
          resize: "none",
          // Apply scrollbar styles only when at max height
          ...(isAtMaxHeight ? webScrollbarStyles : {})
        }
      : {};

  const inputStyle = [
    styles.textInput,
    {
      color: colors.onSurface,
      fontSize: typography.fontSizes.medium,
      fontFamily: typography.fontFamily.regular,
      lineHeight
    },
    Platform.OS === "web" && isFocused && { outlineWidth: 0, outline: "none" },
    webScrollbarStyle
  ];

  const containerStyle = [
    styles.container,
    {
      borderColor: isFocused ? colors.primary : colors.outlineVariant,
      backgroundColor: colors.surfaceContainer,
      height: inputHeight
    },
    style // Parent can override container styles (margins, borders, width)
  ];

  return (
    <View style={containerStyle}>
      <RNTextInput
        ref={inputRef}
        style={inputStyle}
        value={inputText}
        onChangeText={onChangeText}
        placeholder={placeholderText}
        placeholderTextColor={colors.onSurfaceMuted}
        multiline={true}
        numberOfLines={1}
        maxLength={maxLength}
        onSubmitEditing={isDesktopWeb ? undefined : onSubmitEditing}
        onKeyPress={isDesktopWeb ? handleKeyPress : undefined}
        submitBehavior="submit"
        onContentSizeChange={handleContentSizeChange}
        scrollEnabled={inputHeight >= MAX_HEIGHT}
        textAlignVertical="top"
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderWidth: layout.borderWidth.thin,
    borderRadius: layout.borderRadius.round,
    overflow: Platform.OS === "web" ? "hidden" : "hidden"
  },
  textInput: {
    flex: 1,
    paddingHorizontal: layout.space.lg,
    paddingVertical: layout.space.md
  }
});
